import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { ArrowRight, ArrowLeft, Menu, X, Send } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function ContactPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;
    setStatus("sending");
    try {
      await axios.post(`${BACKEND_URL}/api/contact/submit`, { ...form, subject: "Website contact" });
      setStatus("sent");
      setForm({ name: "", email: "", message: "" });
      setTimeout(() => setStatus("idle"), 5000);
    } catch {
      setStatus("error");
      setTimeout(() => setStatus("idle"), 4000);
    }
  };

  return (
    <div style={{ fontFamily: "Manrope,sans-serif" }} className="bg-[#FAFAFA] text-zinc-900 min-h-screen flex flex-col">
      <Helmet>
        <title>Contact — PricePAL Compare</title>
        <meta name="description" content="Get in touch with the PricePAL Compare team." />
      </Helmet>

      {/* Header */}
      <header className="fixed top-0 left-0 w-full z-50 bg-white/90 backdrop-blur-xl border-b border-zinc-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
          <a href="/" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-emerald-600 flex items-center justify-center"><span className="text-white font-black text-xs" style={{ fontFamily: "Outfit,sans-serif" }}>P</span></div>
            <span className="text-base font-bold text-zinc-900" style={{ fontFamily: "Outfit,sans-serif" }}>PricePAL Compare</span>
          </a>
          <div className="flex items-center gap-3">
            <a href="/register" className="bg-emerald-600 text-white hover:bg-emerald-700 font-bold px-4 py-2 rounded-full text-sm transition flex items-center gap-1.5 shadow-sm">
              Get app <ArrowRight className="w-3.5 h-3.5" />
            </a>
            <button className="md:hidden p-1.5 text-zinc-600" onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu">
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
          <nav className="hidden md:flex items-center gap-7 ml-8">
            <a href="/#how-it-works" className="text-sm font-medium text-zinc-500 hover:text-emerald-600 transition">How it works</a>
            <a href="/#fuel" className="text-sm font-medium text-zinc-500 hover:text-emerald-600 transition">Fuel</a>
            <a href="/community" className="text-sm font-medium text-zinc-500 hover:text-emerald-600 transition">Community</a>
            <a href="/about" className="text-sm font-medium text-zinc-500 hover:text-emerald-600 transition">About</a>
            <a href="/contact" className="text-sm font-medium text-emerald-600 font-bold">Contact</a>
          </nav>
        </div>
        {menuOpen && (
          <div className="md:hidden bg-white border-t border-zinc-100 px-4 py-4 space-y-3">
            <a href="/#how-it-works" className="block text-sm font-medium text-zinc-600 py-2">How it works</a>
            <a href="/#fuel" className="block text-sm font-medium text-zinc-600 py-2">Fuel</a>
            <a href="/community" className="block text-sm font-medium text-zinc-600 py-2">Community</a>
            <a href="/about" className="block text-sm font-medium text-zinc-600 py-2">About</a>
            <a href="/contact" className="block text-sm font-bold text-emerald-600 py-2">Contact</a>
          </div>
        )}
      </header>

      <div className="h-14" />

      {/* Content */}
      <main className="flex-1 max-w-lg mx-auto w-full px-4 sm:px-6 py-10 md:py-16">
        <a href="/" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-emerald-600 transition mb-6" data-testid="contact-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </a>
        <h1 className="text-2xl sm:text-3xl tracking-tight font-black text-zinc-900 mb-2" style={{ fontFamily: "Outfit,sans-serif" }} data-testid="contact-heading">
          Contact us
        </h1>
        <p className="text-sm text-zinc-500 mb-6">
          <a href="mailto:support@pricepalcompare.com" className="text-emerald-600 hover:underline font-medium">support@pricepalcompare.com</a>
        </p>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
          <div>
            <label htmlFor="name" className="block text-sm font-semibold text-zinc-700 mb-1">Name</label>
            <input id="name" type="text" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              className="w-full px-4 py-3 border border-zinc-200 rounded-xl text-base focus:outline-none focus:border-emerald-500 transition bg-white"
              placeholder="Your name" data-testid="contact-name" />
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-zinc-700 mb-1">Email</label>
            <input id="email" type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
              className="w-full px-4 py-3 border border-zinc-200 rounded-xl text-base focus:outline-none focus:border-emerald-500 transition bg-white"
              placeholder="you@example.com" data-testid="contact-email" />
          </div>
          <div>
            <label htmlFor="message" className="block text-sm font-semibold text-zinc-700 mb-1">Message</label>
            <textarea id="message" required rows={4} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
              className="w-full px-4 py-3 border border-zinc-200 rounded-xl text-base focus:outline-none focus:border-emerald-500 transition bg-white resize-none"
              placeholder="How can we help?" data-testid="contact-message" />
          </div>

          {status === "sent" && <p className="text-sm text-emerald-600 font-medium" data-testid="contact-success">Message sent! We'll get back to you soon.</p>}
          {status === "error" && <p className="text-sm text-red-500 font-medium" data-testid="contact-error">Something went wrong. Please try again.</p>}

          <button type="submit" disabled={status === "sending"}
            className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-bold py-3.5 rounded-full text-base transition min-h-[48px]"
            data-testid="contact-submit">
            {status === "sending" ? "Sending..." : <><Send className="w-4 h-4" /> Send message</>}
          </button>
        </form>
      </main>

      {/* Footer */}
      <footer className="bg-zinc-50 py-8 border-t border-zinc-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-xs text-zinc-400">&copy; 2026 PricePAL Compare Ltd.</span>
          <nav className="flex gap-5">
            {[{ label: "About", href: "/about" }, { label: "Contact", href: "/contact" }, { label: "Privacy", href: "/privacy" }, { label: "Terms", href: "/terms" }].map(l => (
              <a key={l.href} href={l.href} className="text-sm text-zinc-400 hover:text-emerald-600 transition py-2 min-h-[44px] flex items-center">{l.label}</a>
            ))}
          </nav>
        </div>
      </footer>
    </div>
  );
}
